import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';

import Landing from './pages/Landing';
import Signup from './pages/Signup';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Welcome from './pages/Welcome';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import About from './pages/About';
import Help from './pages/Help';
import Home from './pages/Home';
import ProfileEdit from './pages/ProfileEdit';
import ProfileMe from './pages/ProfileMe';
import ProfileDocuments from './pages/ProfileDocuments';
import OnboardingSchool from './pages/OnboardingSchool';
import OnboardingMoveIn from './pages/OnboardingMoveIn';
import OnboardingBudget from './pages/OnboardingBudget';
import OnboardingPlaceType from './pages/OnboardingPlaceType';
import OnboardingLivingSituation from './pages/OnboardingLivingSituation';
import Search from './pages/SearchResults';
import Saved from './pages/SavedListings';
import Alerts from './pages/Alerts';
import MessagesPlaceholder from './pages/MessagesPlaceholder';
import Roommates from './pages/Roommates';
import Settings from './pages/Settings';
import TrustScore from './pages/TrustScore';
import ListingWizard from './pages/ListingWizard';
import ListingDetail from './pages/ListingDetail';
import LandlordOnly from './pages/LandlordOnly';
import AdminListings from './pages/AdminListings';
import AdminListingReview from './pages/AdminListingReview';

import './App.css';

const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
  </div>
);

/**
 * Protected route — logged-in users only.
 * Logged-out users are redirected to /signup with ?next=<current path> so
 * post-signup they land exactly where they were trying to go.
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signup?next=${next}`} replace />;
  }
  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (user) {
    // Honor `?next` so returnTo after signup/login works.
    const params = new URLSearchParams(location.search);
    const nextParam = params.get('next');
    if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) {
      return <Navigate to={nextParam} replace />;
    }
    return <Navigate to={user.onboarding_completed ? '/home' : '/welcome'} replace />;
  }
  return children;
};

/**
 * Landing route — "/" renders the marketing page for logged-out users but
 * redirects logged-in users to /home (or /welcome if mid-onboarding). Logged-in
 * users should never accidentally see the marketing site.
 */
const LandingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) {
    return <Navigate to={user.onboarding_completed ? '/home' : '/welcome'} replace />;
  }
  return <Landing />;
};

const AppRouter = () => {
  const location = useLocation();

  // Emergent Google OAuth: detect #session_id synchronously during render,
  // BEFORE any protected routes or /auth/me check runs.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
      <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/about" element={<About />} />
      <Route path="/help" element={<Help />} />

      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route path="/onboarding/school" element={<ProtectedRoute><OnboardingSchool /></ProtectedRoute>} />
      <Route path="/onboarding/move-in" element={<ProtectedRoute><OnboardingMoveIn /></ProtectedRoute>} />
      <Route path="/onboarding/budget" element={<ProtectedRoute><OnboardingBudget /></ProtectedRoute>} />
      <Route path="/onboarding/place-type" element={<ProtectedRoute><OnboardingPlaceType /></ProtectedRoute>} />
      <Route path="/onboarding/living-situation" element={<ProtectedRoute><OnboardingLivingSituation /></ProtectedRoute>} />

      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/profile/me" element={<ProtectedRoute><ProfileMe /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
      <Route path="/profile/documents" element={<ProtectedRoute><ProfileDocuments /></ProtectedRoute>} />
      <Route path="/trust-score" element={<ProtectedRoute><TrustScore /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><Saved /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPlaceholder /></ProtectedRoute>} />
      <Route path="/roommates" element={<ProtectedRoute><Roommates /></ProtectedRoute>} />
      <Route path="/roommates/matches" element={<ProtectedRoute><Roommates /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/privacy" element={<Navigate to="/settings#privacy" replace />} />

      {/* Listings — Phase 1 */}
      <Route path="/landlord-only" element={<ProtectedRoute><LandlordOnly /></ProtectedRoute>} />
      <Route path="/listings/new" element={<ProtectedRoute><ListingWizard /></ProtectedRoute>} />
      <Route path="/listings/new/:listing_id/:step" element={<ProtectedRoute><ListingWizard /></ProtectedRoute>} />
      <Route path="/listings/:listing_id" element={<ProtectedRoute><ListingDetail /></ProtectedRoute>} />
      <Route path="/admin/listings" element={<ProtectedRoute><AdminListings /></ProtectedRoute>} />
      <Route path="/admin/listings/:listing_id/review" element={<ProtectedRoute><AdminListingReview /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <AppRouter />
          <Toaster
            position="bottom-right"
            toastOptions={{
              classNames: {
                // Brand-matching: neutral white background, amber accent for
                // success, rose for error. NOT green/emerald.
                toast: 'group toast bg-white border border-slate-200 text-slate-900 shadow-lg rounded-xl',
                description: 'text-slate-600',
                success:
                  'border-amber-300 bg-amber-50 text-amber-900 [&_[data-icon]]:text-amber-600',
                error:
                  'border-rose-300 bg-rose-50 text-rose-900 [&_[data-icon]]:text-rose-600',
                info: 'border-slate-200 bg-white text-slate-900',
                warning:
                  'border-amber-300 bg-amber-50 text-amber-900 [&_[data-icon]]:text-amber-600',
                actionButton:
                  'bg-amber-500 text-white rounded-full px-3 py-1 text-xs font-semibold',
                cancelButton:
                  'bg-slate-100 text-slate-700 rounded-full px-3 py-1 text-xs font-semibold',
              },
            }}
          />
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
